import React from "react";
import {
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Save as SaveIcon,
  HelpOutline as HelpIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material";
import type { PolicyDef } from "./policies.data";

/**
 * Componentes PolicyRow + SaveResetActions extraídos de
 * `ConfiguracionDistribucionPage` para mantener la página ≤500L (P7).
 * Sin cambio funcional vs versión inline.
 */

interface PolicyRowProps {
  def: PolicyDef;
  value: string;
  initial: string;
  onChange: (v: string) => void;
  onSave: () => void;
  onReset: () => void;
  saving: boolean;
}

export function PolicyRow({
  def,
  value,
  initial,
  onChange,
  onSave,
  onReset,
  saving,
}: PolicyRowProps): React.ReactElement {
  const dirty = value !== initial;

  if (def.options) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Stack sx={{ minWidth: 280 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" fontWeight={500}>
              {def.label}
            </Typography>
            <Tooltip title={def.description}>
              <HelpIcon fontSize="inherit" color="action" />
            </Tooltip>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {def.key}
          </Typography>
        </Stack>
        <TextField
          select
          size="small"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          SelectProps={{ native: true }}
          sx={{ minWidth: 180 }}
        >
          <option value="">—</option>
          {def.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </TextField>
        <SaveResetActions
          dirty={dirty}
          saving={saving}
          onSave={onSave}
          onReset={onReset}
        />
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Stack sx={{ minWidth: 280 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body2" fontWeight={500}>
            {def.label}
          </Typography>
          <Tooltip title={def.description}>
            <HelpIcon fontSize="inherit" color="action" />
          </Tooltip>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {def.key}{" "}
          {def.min !== undefined && def.max !== undefined
            ? `(${def.min} → ${def.max})`
            : ""}
        </Typography>
      </Stack>
      {def.type === "string_array" ? (
        <TextField
          type="text"
          size="small"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="A115, A715, A125"
          helperText="separados por coma"
          inputProps={{ "aria-label": def.label }}
          sx={{ minWidth: 240 }}
        />
      ) : (
        <TextField
          type="number"
          size="small"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputProps={{
            min: def.min,
            max: def.max,
            step: def.type === "int" ? 1 : 0.01,
            "aria-label": def.label,
          }}
          sx={{ minWidth: 180 }}
        />
      )}
      <SaveResetActions
        dirty={dirty}
        saving={saving}
        onSave={onSave}
        onReset={onReset}
      />
    </Stack>
  );
}

function SaveResetActions({
  dirty,
  saving,
  onSave,
  onReset,
}: {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  onReset: () => void;
}): React.ReactElement {
  return (
    <>
      <Tooltip title={dirty ? "Guardar cambio" : "Sin cambios"}>
        <span>
          <IconButton
            size="small"
            color="primary"
            disabled={!dirty || saving}
            onClick={onSave}
            aria-label="Guardar"
          >
            <SaveIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={dirty ? "Descartar cambio" : "Sin cambios"}>
        <span>
          <IconButton
            size="small"
            disabled={!dirty || saving}
            onClick={onReset}
            aria-label="Descartar"
          >
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );
}
